import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch {}
      if (attempts >= 8) { setStatus("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="min-h-[60vh] flex items-center justify-center adminia-fade-in">
      <Card className="p-10 text-center max-w-md w-full">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-brand mx-auto animate-spin mb-4" />
            <h1 className="font-heading font-bold text-xl">Confirmation du paiement…</h1>
            <p className="text-muted-foreground mt-1">Un instant, nous validons votre abonnement.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-success mx-auto mb-4" />
            <h1 className="font-heading font-bold text-2xl">Bienvenue en Premium !</h1>
            <p className="text-muted-foreground mt-2 mb-6">
              Toutes les fonctionnalités sont désormais débloquées. Merci de votre confiance.
            </p>
            <Button className="rounded-full bg-brand hover:bg-brand/90 h-12 px-8" onClick={() => navigate("/app")} data-testid="success-continue">
              Accéder à mon espace
            </Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="h-14 w-14 text-danger mx-auto mb-4" />
            <h1 className="font-heading font-bold text-xl">Paiement non confirmé</h1>
            <p className="text-muted-foreground mt-2 mb-6">
              Le paiement n'a pas pu être vérifié. Vous pouvez réessayer.
            </p>
            <Button variant="outline" className="rounded-full h-12 px-8" onClick={() => navigate("/app/abonnement")} data-testid="success-retry">
              Retour aux offres
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
